import React from 'react';
import {
  Box,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Zoom,
  useTheme
} from '@mui/material';
import { PlanTask } from '@/store/Employee/DailyPlanStore';
import {
  taskCardSx,
  segmentedGroupSx,
  formatPlanDate,
  DecisionTone
} from './ritualStyles';

export interface DecisionOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  tone: Exclude<DecisionTone, 'neutral'>;
}

export interface TaskDecisionCardProps {
  task: PlanTask;
  options: DecisionOption[];
  /** '' = undecided. */
  value: string;
  onChange: (value: string) => void;
  reason: string;
  onReasonChange: (reason: string) => void;
  /** Selecting this option reveals the (mandatory) reason field. */
  reasonRequiredFor: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  /** Group aria-label, e.g. "Decision for task: Fix the carry-over bug". */
  groupLabel: string;
  /** Drives the mount stagger. */
  index: number;
}

/**
 * A single task awaiting a decision — used for BOTH carry-over triage
 * (Continue / Drop) and the EOD status pass (Completed / Not completed), so the
 * two read as the same kind of object.
 *
 * The card tints to match the choice, but color is never the only signal: the
 * selected pill always carries a text label.
 */
const TaskDecisionCard: React.FC<TaskDecisionCardProps> = ({
  task,
  options,
  value,
  onChange,
  reason,
  onReasonChange,
  reasonRequiredFor,
  reasonLabel,
  reasonPlaceholder,
  groupLabel,
  index
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const titleId = React.useId();

  const selected = options.find((o) => o.value === value);
  const state: DecisionTone = selected ? selected.tone : 'neutral';
  const needsReason = value === reasonRequiredFor;

  return (
    <Zoom in timeout={300} style={{ transitionDelay: `${Math.min(index, 8) * 50}ms` }}>
      <Box component="section" aria-labelledby={titleId} sx={taskCardSx(theme, isDark, state)}>
        <Typography
          id={titleId}
          component="h3"
          sx={{ fontWeight: 600, fontSize: '0.975rem', lineHeight: 1.4 }}
        >
          {task.title}
        </Typography>

        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {task.description}
          </Typography>
        )}

        {task.plan_date && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.75, fontWeight: 500 }}
          >
            From {formatPlanDate(task.plan_date)}
          </Typography>
        )}

        {task.incomplete_reason && (
          <Box
            sx={{
              mt: 1,
              pl: 1.25,
              borderLeft: '2px solid',
              borderColor: 'divider'
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', display: 'block' }}
            >
              Last time: “{task.incomplete_reason}”
            </Typography>
          </Box>
        )}

        <ToggleButtonGroup
          size="small"
          exclusive
          value={value || null}
          onChange={(_e, val) => val && onChange(val)}
          aria-label={groupLabel}
          sx={segmentedGroupSx(isDark)}
        >
          {options.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value} data-tone={opt.tone}>
              {opt.icon}
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Collapse in={needsReason} unmountOnExit>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            size="small"
            autoFocus
            label={reasonLabel}
            placeholder={reasonPlaceholder}
            value={reason || ''}
            onChange={(e) => onReasonChange(e.target.value)}
            inputProps={{ 'aria-required': true }}
            sx={{
              mt: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper'
              }
            }}
          />
        </Collapse>
      </Box>
    </Zoom>
  );
};

export default TaskDecisionCard;
